import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react/dist/iconify.js'

import Header from '../../components/Header/Header'
import { merchantApi } from '../../services/merchantApi'

import './ConfigSettingPage.scss'

const INITIAL_SETTINGS = {
    maintenanceMode: false,
    autoAcceptOrders: true,
    taxFlag: 'exclusive',
    sstRate: null,
    serviceCharge: null,
}

const normalizeSettingsResponse = (response) => {
    const data = response?.data ?? response ?? {}
    const apiSettings = data.settings ?? {}

    return {
        settings: {
            maintenanceMode: apiSettings.maintenance_mode ?? false,
            autoAcceptOrders: apiSettings.auto_accept_orders ?? false,
            taxFlag: apiSettings.default_tax_flag ?? false,
            sstRate: apiSettings.default_sst_rate ?? 0,
            serviceCharge: apiSettings.service_charge ?? 0,
        },
        platform: {
            name: apiSettings.platform_name ?? null,
            waveApiVersion: apiSettings.wave_api_version ?? null,
            lastUpdatedAt: data.last_updated_at ?? null,
            lastUpdatedBy: data.last_updated_by ?? '',
        },
    }
}

const formatLastUpdated = (value) => {
    if (!value) return '—'

    const date = new Date(value.replace(' ', 'T'))
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat('en-MY', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(date)
}

function Toggle({ checked, onChange, label, disabled = false }) {
    return (
        <label className={`config-toggle ${disabled ? 'config-toggle--disabled' : ''}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                aria-label={label}
                disabled={disabled}
            />
            <span className="config-toggle__track" aria-hidden="true" />
        </label>
    )
}

export default function ConfigSettingPage() {
    const [settings, setSettings] = useState(INITIAL_SETTINGS)
    const [savedSettings, setSavedSettings] = useState(INITIAL_SETTINGS)
    const [platform, setPlatform] = useState()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    useEffect(() => {
        let active = true

        const loadSettings = async () => {
            setLoading(true)
            setError('')

            try {
                const response = await merchantApi.settings.get()
                if (!active) return

                const normalized = normalizeSettingsResponse(response)
                setSettings(normalized.settings)
                setSavedSettings(normalized.settings)
                setPlatform(normalized.platform)
            } catch (requestError) {
                if (active) {
                    setError(requestError?.message || 'Unable to load configuration settings.')
                }
            } finally {
                if (active) setLoading(false)
            }
        }

        loadSettings()

        return () => {
            active = false
        }
    }, [])

    const updateSetting = (name, value) => {
        setSettings((current) => ({ ...current, [name]: value }))
        setSuccessMessage('')
    }

    const hasChanges = JSON.stringify(settings) !== JSON.stringify(savedSettings)

    const handleSubmit = async (event) => {
        event.preventDefault()
        // if (!hasChanges || loading || saving) return

        setSaving(true)
        setError('')
        setSuccessMessage('')

        try {
            const response = await merchantApi.settings.update({
                maintenance_mode: settings.maintenanceMode,
                auto_accept_orders: settings.autoAcceptOrders,
                default_tax_flag: settings.taxFlag,
                default_sst_rate: Number(settings.sstRate),
                service_charge: Number(settings.serviceCharge),
            })
            const normalized = normalizeSettingsResponse(response)

            setSettings(normalized.settings)
            setSavedSettings(normalized.settings)
            setPlatform(normalized.platform)
            setSuccessMessage(response?.message || 'Settings updated successfully.')
        } catch (requestError) {
            setError(requestError?.message || 'Unable to update configuration settings.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="config-setting-page">
            <Header
                items={[
                    { label: 'Cuscapi', to: '/' },
                    { label: 'Config & settings', current: true },
                ]}
            />
            <div className="config-setting-page__heading">
                <div>
                    <h1>Config &amp; settings</h1>
                    <p>Platform-wide configuration · Changes apply immediately</p>
                </div>
                <button
                    className="config-setting-page__save"
                    type="submit"
                    disabled={loading || saving}
                    onClick={handleSubmit}
                >
                    {saving ? 'Saving...' : 'Save changes'}
                </button>
            </div>

            {error && <div className="config-setting-page__message config-setting-page__message--error" role="alert">{error}</div>}
            {successMessage && <div className="config-setting-page__message config-setting-page__message--success" role="status">{successMessage}</div>}

            <div className="config-setting-page__grid">
                <section className="config-card" aria-labelledby="platform-settings-title">
                    <h2 className="config-card__title" id="platform-settings-title">Platform</h2>

                    <div className="config-card__platform-details">
                        <dl>
                            <div>
                                <dt>Platform name</dt>
                                <dd>{loading ? 'Loading...' : platform?.name || '-'}</dd>
                            </div>
                            <div>
                                <dt>WAVE API version</dt>
                                <dd>{loading ? 'Loading...' : platform?.waveApiVersion || '-'}</dd>
                            </div>
                            <div>
                                <dt>Last updated</dt>
                                <dd>
                                    {loading ? 'Loading...' : formatLastUpdated(platform?.lastUpdatedAt)}
                                    {!loading && platform?.lastUpdatedBy && <> by <strong>{platform?.lastUpdatedBy}</strong></>}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className="config-card__setting-row">
                        <div>
                            <h3>Maintenance mode</h3>
                            <p>Customers see a maintenance page on all storefronts. Admin portal is unaffected.</p>
                        </div>
                        <Toggle
                            label="Maintenance mode"
                            checked={settings.maintenanceMode}
                            onChange={(checked) => updateSetting('maintenanceMode', checked)}
                            disabled={loading || saving}
                        />
                    </div>

                    <div className="config-card__setting-row">
                        <div>
                            <h3>Auto-accept orders (default)</h3>
                            <p>Default setting applied when a new merchant is provisioned. Can be overridden per merchant.</p>
                        </div>
                        <Toggle
                            label="Auto-accept orders by default"
                            checked={settings.autoAcceptOrders}
                            onChange={(checked) => updateSetting('autoAcceptOrders', checked)}
                            disabled={loading || saving}
                        />
                    </div>
                </section>

                <section className="config-card config-card--billing" aria-labelledby="billing-settings-title">
                    <h2 className="config-card__title" id="billing-settings-title">Tax &amp; billing defaults</h2>

                    <div className="config-card__billing-content">
                        <fieldset className="config-tax-options">
                            <legend>Default tax flag</legend>
                            <div className="config-tax-options__grid">
                                {[
                                    { value: 'exclusive', title: 'Exclusive', description: 'Tax added on top' },
                                    { value: 'inclusive', title: 'Inclusive', description: 'Tax baked in' },
                                ].map((option) => (
                                    <label
                                        className={`config-tax-option ${settings.taxFlag === option.value ? 'config-tax-option--selected' : ''}`}
                                        key={option.value}
                                    >
                                        <span>
                                            <strong>{option.title}</strong>
                                            <small>{option.description}</small>
                                        </span>
                                        <input
                                            type="radio"
                                            name="taxFlag"
                                            value={option.value}
                                            checked={settings.taxFlag === option.value}
                                            onChange={(event) => updateSetting('taxFlag', event.target.value)}
                                            disabled={loading || saving}
                                        />
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        <div className="config-rate-grid">
                            <label className="config-rate-field">
                                <span>Default SST rate</span>
                                <div className="config-rate-field__input">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={settings.sstRate}
                                        onChange={(event) => updateSetting('sstRate', event.target.value)}
                                        disabled={loading || saving}
                                    />
                                    <Icon icon="ph:percent" height={16}/>
                                </div>
                            </label>

                            <label className="config-rate-field">
                                <span>Service charge</span>
                                <div className="config-rate-field__input">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={settings.serviceCharge}
                                        onChange={(event) => updateSetting('serviceCharge', event.target.value)}
                                        disabled={loading || saving}
                                    />
                                    <Icon icon="ph:percent" height={16}/>
                                </div>
                            </label>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    )
}
